import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { paymentAPI, plansAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import '../styles/PlanSelection.css';

// Dial codes are stable ITU-assigned prefixes, not part of AshtechPay's catalog — safe to keep as a static lookup.
const DIAL_CODES = {
  BJ: '+229', BF: '+226', CM: '+237', CF: '+236', CG: '+242',
  CI: '+225', GA: '+241', ML: '+223', NE: '+227', CD: '+243',
  SN: '+221', TD: '+235', TG: '+228',
};

const flagEmoji = (code) => code.toUpperCase().replace(/./g, (ch) => String.fromCodePoint(127397 + ch.charCodeAt(0)));

// Used only until the live catalog loads (or if that fetch fails) — never the source of truth.
const FALLBACK_COUNTRIES = [
  { code: 'CM', name: 'Cameroun', currency: 'XAF', operators: ['MTN Money', 'Orange Money'], unavailable_operators: [] },
];

const PLAN_FEATURES = {
  basic:      { en: ['15 travel guides', 'Interactive visa checker by passport', 'Visa refusal: reasons & fixes', 'Airport, transit & free Touristanbul tour', 'Attractions & 2026 entrance fees', 'Food, halal dining & prices', 'Weather, packing & 2027 holidays', 'Health, pharmacies & insurance', 'Money, SIM, apps & customs rules'], fr: ['15 guides de voyage', 'Vérificateur de visa interactif par passeport', 'Visa refusé : causes & solutions', 'Aéroport, transit & visite gratuite Touristanbul', 'Sites & tarifs d\'entrée 2026', 'Cuisine, repas halal & prix', 'Météo, valise & jours fériés 2027', 'Santé, pharmacies & assurance', 'Argent, SIM, applis & douane'] },
  premium:    { en: ['All 24 travel guides', 'Budget calculator (TL / EUR / XAF)', '3, 5 & 7-day itineraries', 'Cappadocia extension & balloon prices', 'Bosphorus, islands & day trips', 'Hammams, mosques & Ramadan guide', 'Shopping, bargaining & tax-free', 'Study in Istanbul & medical tourism', 'Families, women & solo travellers', 'Price list & district comparison'], fr: ['Les 24 guides de voyage', 'Calculateur de budget (TL / EUR / XAF)', 'Itinéraires 3, 5 & 7 jours', 'Extension Cappadoce & prix des montgolfières', 'Bosphore, îles & excursions', 'Guide hammams, mosquées & Ramadan', 'Shopping, marchandage & détaxe', 'Études à Istanbul & tourisme médical', 'Familles, femmes & voyageurs solo', 'Liste de prix & comparatif quartiers'] },
  enterprise: { en: ['All 30 guides — everything in Navigator', 'Wholesale sourcing module & supplier checklist', 'Trade-fair calendar 2026–2027', 'Opening a company in Türkiye', 'Work permits & 2026 salary rules', 'Buying property step by step', 'Banking, tax number & safe payments', 'Residence permits & citizenship by investment', 'Turkish phrase trainer & trip roadmap'], fr: ['Les 30 guides — tout du Navigateur', 'Module achats en gros & vérification fournisseurs', 'Calendrier des salons 2026–2027', 'Créer une société en Türkiye', 'Permis de travail & règles salariales 2026', 'Acheter un bien étape par étape', 'Banque, numéro fiscal & paiements sûrs', 'Permis de séjour & citoyenneté par investissement', 'Phrases turques & feuille de route'] },
};

const PLAN_NAMES = { basic: { en: 'Explorer', fr: 'Explorateur' }, premium: { en: 'Navigator', fr: 'Navigateur' }, enterprise: { en: 'Pathfinder', fr: 'Pionnier' } };
const LEVELS = ['basic', 'premium', 'enterprise'];
const FALLBACK_PRICES = { basic: 6500, premium: 16500, enterprise: 39500 };
const DASHBOARDS = { basic: '/basic-dashboard', premium: '/premium-dashboard', enterprise: '/enterprise-dashboard' };

const PlanSelection = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);

  const planNameRaw = params.get('plan') || 'Explorer';
  const planLower = planNameRaw.toLowerCase();
  const levelParam = params.get('level');
  const planAccessLevel = LEVELS.includes(levelParam) ? levelParam
    : planLower.includes('pathfinder') || planLower.includes('pionnier') || planLower.includes('enterprise') ? 'enterprise'
    : planLower.includes('navigat') || planLower.includes('premium') ? 'premium' : 'basic';

  // Display only — the server always charges the price stored in the database.
  const [price, setPrice] = useState(FALLBACK_PRICES[planAccessLevel]);
  const currency = 'XAF';
  const priceLabel = Math.round(price).toLocaleString();

  useEffect(() => {
    plansAPI.getAll()
      .then(res => {
        const plan = (Array.isArray(res.data) ? res.data : []).find(p => p.accessLevel === planAccessLevel);
        if (plan) setPrice(Number(plan.price));
      })
      .catch(() => {});
  }, [planAccessLevel]);

  const { isAuthenticated, upgrade } = useAuth();

  // New visitors create their account next; signed-in users get their plan upgraded/renewed.
  const finishPayment = async (ref) => {
    if (!isAuthenticated) {
      navigate('/signup', { state: { planAccessLevel, paymentReference: ref } });
      return;
    }
    try {
      const updated = await upgrade(ref);
      navigate(DASHBOARDS[updated.plan] || '/plans');
    } catch (err) {
      setPhase('failed');
      setStatus(err?.response?.data?.message || (lang === 'fr' ? 'Paiement reçu mais mise à jour du forfait impossible. Contactez le support avec la référence ' + ref : 'Payment received but the plan could not be updated. Contact support with reference ' + ref));
      setStatusType('error');
    }
  };

  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('fr') ? 'fr' : 'en';
  const planLabel = PLAN_NAMES[planAccessLevel][lang] || planNameRaw;
  const features = PLAN_FEATURES[planAccessLevel][lang];

  const [countries, setCountries] = useState(FALLBACK_COUNTRIES);
  const [country, setCountry]   = useState(FALLBACK_COUNTRIES[0].code);
  const [operator, setOperator] = useState('');
  const [phone, setPhone]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [status, setStatus]     = useState('');
  const [statusType, setStatusType] = useState('');
  const [phase, setPhase]       = useState('idle');
  const [reference, setReference] = useState('');
  const [countdown, setCountdown] = useState(120);
  const [showManualBtn, setShowManualBtn] = useState(false);

  const pollRef    = useRef(null);
  const timerRef   = useRef(null);
  const manualRef  = useRef(null);
  const countRef   = useRef(120);

  const selectedCountry = countries.find(c => c.code === country) || countries[0];
  const availableOperators = (selectedCountry?.operators || []).filter(
    op => !(selectedCountry?.unavailable_operators || []).includes(op)
  );

  const selectCountry = (code) => {
    setCountry(code);
    const c = countries.find(x => x.code === code);
    const avail = (c?.operators || []).filter(op => !(c?.unavailable_operators || []).includes(op));
    setOperator(avail[0] || '');
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await paymentAPI.countries();
        const list = Array.isArray(res.data) ? res.data : [];
        if (!cancelled && list.length) {
          setCountries(list);
          const preferred = list.find(c => c.code === country) || list[0];
          selectCountry(preferred.code);
        }
      } catch {
        // keep FALLBACK_COUNTRIES
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!operator && availableOperators.length) setOperator(availableOperators[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry]);

  const stopAll = () => {
    clearInterval(pollRef.current);
    clearInterval(timerRef.current);
    clearTimeout(manualRef.current);
  };

  const handleCancel = () => { stopAll(); setPhase('idle'); setStatus(''); setShowManualBtn(false); setCountdown(120); };

  const startPolling = (ref) => {
    let attempts = 0;
    countRef.current = 120;

    timerRef.current = setInterval(() => {
      countRef.current -= 1;
      setCountdown(countRef.current);
      if (countRef.current <= 0) { clearInterval(timerRef.current); }
    }, 1000);

    manualRef.current = setTimeout(() => setShowManualBtn(true), 20000);

    pollRef.current = setInterval(async () => {
      attempts++;
      try {
        const res = await paymentAPI.status(ref);
        const st = res.data?.status;
        if (st === 'SUCCESS') {
          stopAll();
          setPhase('confirmed');
          setStatus(lang === 'fr' ? 'Paiement confirmé ! Configuration de votre compte...' : 'Payment confirmed! Setting up your account...');
          setStatusType('success');
          setTimeout(() => finishPayment(ref), 1500);
        } else if (st === 'FAILED') {
          stopAll();
          setPhase('failed');
          setStatus(lang === 'fr' ? 'Paiement échoué. Veuillez réessayer.' : 'Payment failed. Please try again.');
          setStatusType('error');
        }
      } catch {}
      if (attempts >= 24) {
        stopAll();
        setStatus(lang === 'fr' ? 'Délai expiré. Si vous avez payé, cliquez sur le bouton ci-dessous.' : 'Timed out. If you paid, use the button below.');
        setStatusType('processing');
        setShowManualBtn(true);
      }
    }, 5000);
  };

  const handlePay = async (e) => {
    e.preventDefault();
    if (!phone.trim()) { setStatus(lang === 'fr' ? 'Veuillez entrer votre numéro de téléphone.' : 'Please enter your phone number.'); setStatusType('error'); return; }
    if (!operator) { setStatus(lang === 'fr' ? 'Aucun opérateur disponible pour ce pays actuellement.' : 'No operator currently available for this country.'); setStatusType('error'); return; }
    setLoading(true);
    setStatus(lang === 'fr' ? 'Initialisation du paiement...' : 'Initiating payment...');
    setStatusType('processing');
    try {
      const dialCode = DIAL_CODES[country] || '';
      const fullPhone = `${dialCode}${phone.replace(/\D/g, '')}`;
      const res = await paymentAPI.process({ planAccessLevel, operator, phone: fullPhone, country });
      if (res.data?.success) {
        const ref = res.data.reference;
        setReference(ref);
        if (res.data.simulated || res.data.immediate) {
          setPhase('confirmed');
          setStatus(lang === 'fr' ? 'Paiement confirmé ! Configuration de votre compte...' : 'Payment confirmed! Setting up your account...');
          setStatusType('success');
          setTimeout(() => finishPayment(ref), 1500);
        } else {
          setPhase('waiting');
          setStatus('');
          startPolling(ref);
        }
      } else {
        setStatus(res.data?.message || (lang === 'fr' ? 'Paiement échoué.' : 'Payment failed.'));
        setStatusType('error');
      }
    } catch (err) {
      setStatus(err?.response?.data?.message || (lang === 'fr' ? 'Erreur de paiement. Réessayez.' : 'Payment error. Please try again.'));
      setStatusType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleManualConfirm = async () => {
    try {
      const res = await paymentAPI.status(reference);
      if (res.data?.status === 'SUCCESS') {
        stopAll();
        setPhase('confirmed');
        setStatus(lang === 'fr' ? 'Paiement confirmé ! Configuration de votre compte...' : 'Payment confirmed! Setting up your account...');
        setStatusType('success');
        setTimeout(() => finishPayment(reference), 1500);
      } else {
        setStatus(lang === 'fr' ? 'Paiement non encore confirmé. Vérifiez votre téléphone.' : 'Payment not yet confirmed. Please check your phone.');
        setStatusType('error');
      }
    } catch {
      setStatus(lang === 'fr' ? 'Erreur de vérification. Réessayez.' : 'Verification error. Please try again.');
      setStatusType('error');
    }
  };

  useEffect(() => () => stopAll(), []);

  const planClass = { basic: 'basic', premium: 'premium', enterprise: 'enterprise' }[planAccessLevel];

  return (
    <div className="ps-page">
      <div className="ps-card">
        {/* LEFT — Plan Summary */}
        <div className="ps-left">
          <div className={`ps-plan-badge ${planClass}`}>
            🕌 {planAccessLevel.toUpperCase()} PLAN
          </div>
          <h1 className="ps-plan-name">{planLabel}</h1>
          <div className="ps-price-block">
            <div className="ps-price-label">{lang === 'fr' ? 'Montant à payer' : 'Amount to pay'}</div>
            <div className="ps-price-amount">
              {priceLabel} <span>{currency}</span>
            </div>
            <div className="ps-price-duration">
              {planAccessLevel === 'basic' ? (lang === 'fr' ? 'Accès 30 jours' : '30-day access') : planAccessLevel === 'premium' ? (lang === 'fr' ? 'Accès 90 jours' : '90-day access') : (lang === 'fr' ? 'Accès 1 an' : '1-year access')}
            </div>
          </div>
          <div className="ps-features">
            <h4>{lang === 'fr' ? 'Ce qui est inclus' : "What's included"}</h4>
            <ul>
              {features.map((f, i) => (
                <li key={i}><span className={`ps-check ${planClass}`}>✓</span>{f}</li>
              ))}
            </ul>
          </div>
          <div className="ps-trust">
            <div className="ps-trust-item">🔒 {lang === 'fr' ? 'Paiement chiffré 256 bits' : '256-bit encrypted payment'}</div>
            <div className="ps-trust-item">⚡ {lang === 'fr' ? 'Accès instantané après paiement' : 'Instant access after payment'}</div>
            <div className="ps-trust-item">🌐 {lang === 'fr' ? 'Support bilingue EN / FR' : 'Bilingual support EN / FR'}</div>
          </div>
        </div>

        {/* RIGHT — Payment Form or Waiting */}
        <div className="ps-right">
          {/* Steps */}
          <div className="ps-steps">
            <div className="ps-step done"><div className="ps-step-num">✓</div>{lang === 'fr' ? 'Choisir' : 'Select'}</div>
            <span className="ps-step-sep">›</span>
            <div className={`ps-step ${phase === 'idle' ? 'active' : 'done'}`}><div className="ps-step-num">{phase !== 'idle' ? '✓' : '2'}</div>{lang === 'fr' ? 'Payer' : 'Pay'}</div>
            <span className="ps-step-sep">›</span>
            <div className={`ps-step ${phase === 'confirmed' ? 'active' : ''}`}><div className="ps-step-num">3</div>{lang === 'fr' ? 'Compte' : 'Account'}</div>
          </div>

          {phase === 'waiting' ? (
            <div className="ps-waiting">
              <div className="ps-spinner" />
              <h2>{lang === 'fr' ? 'Confirmez sur Votre Téléphone' : 'Confirm on Your Phone'}</h2>
              <p>{lang === 'fr' ? `Une demande de paiement a été envoyée au ${phone}. Ouvrez votre application ${operator} ou attendez l'invite USSD pour confirmer.` : `A payment request was sent to ${phone}. Open your ${operator} app or wait for the USSD prompt to confirm.`}</p>
              {reference && (
                <div className="ps-ref">
                  <span>{lang === 'fr' ? 'Réf' : 'Ref'}:</span>{reference}
                </div>
              )}
              <div className="ps-countdown">
                {lang === 'fr' ? `Vérification auto dans ${countdown}s` : `Auto-checking in ${countdown}s`}
              </div>
              {status && <div className={`ps-status ${statusType}`}>{status}</div>}
              {showManualBtn && (
                <button className={`ps-pay-btn ${planClass}`} onClick={handleManualConfirm}>
                  ✅ {lang === 'fr' ? 'J\'ai Confirmé le Paiement' : 'I Have Confirmed Payment'}
                </button>
              )}
              <button className="ps-cancel-btn" onClick={handleCancel}>
                {lang === 'fr' ? 'Annuler' : 'Cancel'}
              </button>
            </div>
          ) : (
            <>
              <div className="ps-right-header">
                <h2>{lang === 'fr' ? 'Finaliser Votre Paiement' : 'Complete Your Payment'}</h2>
                <p>{lang === 'fr' ? 'Paiement mobile sécurisé Mobile Money' : 'Secure Mobile Money payment'}</p>
              </div>

              <form onSubmit={handlePay}>
                <div className="ps-field">
                  <label>{lang === 'fr' ? 'Numéro de téléphone Mobile Money' : 'Mobile Money Phone Number'}</label>
                  <div className="ps-field-row">
                    <select className="ps-country-code" value={country} onChange={e => selectCountry(e.target.value)}>
                      {countries.map(c => (
                        <option key={c.code} value={c.code}>{flagEmoji(c.code)} {c.name} {DIAL_CODES[c.code] || ''}</option>
                      ))}
                    </select>
                    <input className="ps-input" type="tel" placeholder={lang === 'fr' ? 'ex. 677 123 456' : 'e.g. 677 123 456'} value={phone} onChange={e => setPhone(e.target.value)} required />
                  </div>
                </div>

                <div className="ps-field">
                  <div className="ps-method-label">{lang === 'fr' ? 'Méthode de paiement' : 'Payment Method'}</div>
                  {availableOperators.length ? (
                    <div className="ps-methods">
                      {availableOperators.map(op => (
                        <button key={op} type="button" className={`ps-method-btn ${operator === op ? 'selected' : ''}`} onClick={() => setOperator(op)}>
                          <div className="ps-method-icon">📱</div>
                          <div>
                            <div className="ps-method-name">{op}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className={`ps-status error`}>
                      {lang === 'fr' ? 'Aucun opérateur disponible pour ce pays actuellement.' : 'No operator currently available for this country.'}
                    </div>
                  )}
                </div>

                {status && phase === 'idle' && <div className={`ps-status ${statusType}`}>{status}</div>}

                <button type="submit" className={`ps-pay-btn ${planClass}`} disabled={loading}>
                  {loading ? (lang === 'fr' ? '⏳ Initialisation...' : '⏳ Processing...') : `💳 ${lang === 'fr' ? 'Payer' : 'Pay'} ${priceLabel} ${currency}`}
                </button>
              </form>

              <p className="ps-fine-print">
                {lang === 'fr' ? 'En payant, vous acceptez nos conditions d\'utilisation. Accès accordé immédiatement après confirmation.' : 'By paying you agree to our terms of service. Access granted immediately after payment confirmation.'}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlanSelection;
